import { DASH_COLOURS, type DashCard as DashCardData, type DashColour } from '@chessmate/game-engine';
import { cn } from '@chessmate/ui';
import { useT } from '../i18n/useT.js';

/**
 * The Colour Dash deck, drawn here (no pictures to load). Each colour has its own SHAPE as well as its colour, printed in the corners and on a
 * "colour chip", so a player who cannot tell the colours apart can still tell the cards apart: red is a circle, yellow a triangle, green a
 * square, blue a diamond. The look is this app's own: a solid colour card with a rounded white badge in the middle.
 */
export const DASH_SHAPE: Record<DashColour, 'circle' | 'triangle' | 'square' | 'diamond'> = {
  red: 'circle',
  yellow: 'triangle',
  green: 'square',
  blue: 'diamond',
};

/** Card and chip backgrounds: literal Tailwind classes so the compiler sees them. */
const FACE: Record<DashColour | 'wild', string> = {
  red: 'bg-red-600 text-white',
  yellow: 'bg-yellow-400 text-slate-900',
  green: 'bg-green-600 text-white',
  blue: 'bg-blue-600 text-white',
  wild: 'bg-slate-800 text-white',
};

/** A small shape in the colour's own shape. Decorative: the card's spoken name carries the meaning. */
export function DashShape({ colour, className }: { colour: DashColour; className?: string }) {
  const shape = DASH_SHAPE[colour];
  return (
    <svg aria-hidden="true" viewBox="0 0 12 12" className={cn('size-3 fill-current', className)}>
      {shape === 'circle' && <circle cx="6" cy="6" r="5" />}
      {shape === 'triangle' && <polygon points="6,1 11,11 1,11" />}
      {shape === 'square' && <rect x="1.5" y="1.5" width="9" height="9" rx="1" />}
      {shape === 'diamond' && <polygon points="6,0.5 11.5,6 6,11.5 0.5,6" />}
    </svg>
  );
}

/** The four shapes in a 2x2 block: the wild cards' emblem. */
function WildEmblem() {
  return (
    <span aria-hidden="true" className="grid grid-cols-2 gap-0.5">
      {DASH_COLOURS.map((c) => (
        <span key={c} className={cn('flex size-4 items-center justify-center rounded-sm', FACE[c])}>
          <DashShape colour={c} className="size-2.5" />
        </span>
      ))}
    </span>
  );
}

/** The centre mark of a card: a number, or a symbol for each action. */
function Centre({ card }: { card: DashCardData }) {
  switch (card.kind) {
    case 'number':
      return <span className="text-3xl font-extrabold leading-none">{card.value}</span>;
    case 'skip':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <circle cx="12" cy="12" r="8" />
          <line x1="6.5" y1="17.5" x2="17.5" y2="6.5" />
        </svg>
      );
    case 'reverse':
      return (
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 9h14l-4-4" />
          <path d="M20 15H6l4 4" />
        </svg>
      );
    case 'draw-two':
      return <span className="text-2xl font-extrabold leading-none">+2</span>;
    case 'wild':
      return <WildEmblem />;
    case 'wild-draw-four':
      return (
        <span className="flex flex-col items-center gap-0.5">
          <span className="text-lg font-extrabold leading-none">+4</span>
          <WildEmblem />
        </span>
      );
  }
}

/** The corner mark: the number or a short symbol, with the colour's shape. */
function cornerText(card: DashCardData): string {
  if (card.kind === 'number') return String(card.value);
  if (card.kind === 'skip') return '⊘';
  if (card.kind === 'reverse') return '⇄';
  if (card.kind === 'draw-two') return '+2';
  return card.kind === 'wild' ? '★' : '+4';
}

/** What a screen reader says for a card: "red 5", "blue skip", "wild draw four". */
export function useDashCardName(): (card: DashCardData) => string {
  const { t } = useT('games');
  return (card) => {
    const colour = card.colour ? t(`cd-colour-${card.colour}`) : '';
    if (card.kind === 'number') return t('cd-card-number', { colour, value: card.value });
    if (card.kind === 'wild') return t('cd-card-wild');
    if (card.kind === 'wild-draw-four') return t('cd-card-wild-draw-four');
    return t(`cd-card-${card.kind}`, { colour });
  };
}

interface DashCardProps {
  card: DashCardData;
  /** Present = the card is a button. */
  onPlay?: () => void;
  /** Drawn but not tappable right now (dimmed). */
  dimmed?: boolean;
}

/** A face-up Colour Dash card: DOM, so it is a real button with a spoken name. */
export function DashCard({ card, onPlay, dimmed = false }: DashCardProps) {
  const name = useDashCardName()(card);
  const className = cn(
    'relative flex h-24 w-16 shrink-0 select-none flex-col items-center justify-center rounded-xl border-2 border-white font-display shadow-md sm:h-28 sm:w-[4.5rem]',
    FACE[card.colour ?? 'wild'],
    dimmed && 'opacity-50',
    onPlay && 'cursor-pointer transition-transform hover:-translate-y-1 focus-visible:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );
  const face = (
    <>
      <span aria-hidden="true" className="absolute left-1 top-1 flex items-center gap-0.5 text-xs font-extrabold leading-none">
        {cornerText(card)}
        {card.colour && <DashShape colour={card.colour} className="size-2.5" />}
      </span>
      <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-2xl bg-white/90 text-slate-900 sm:size-14">
        <Centre card={card} />
      </span>
      {card.colour && <DashShape colour={card.colour} className="absolute bottom-1 right-1 size-3" />}
    </>
  );
  if (onPlay) {
    return (
      <button type="button" className={className} aria-label={name} data-testid={`dash-card-${card.id}`} data-card={card.id} onClick={onPlay}>
        {face}
      </button>
    );
  }
  return (
    <div role="img" className={className} aria-label={name} data-card={card.id}>
      {face}
    </div>
  );
}

/** The colour in force, as a chip with its shape and its name (colour is never the only cue). */
export function ColourChip({ colour, label }: { colour: DashColour; label: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-extrabold', FACE[colour])}>
      <DashShape colour={colour} />
      {label}
    </span>
  );
}
